import { Alert, Badge, Button, Group, Modal, Text } from "@mantine/core";
import { IconAlertTriangle, IconCheck, IconFileSpreadsheet } from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import { useMemo } from "react";
import * as xlsx from "xlsx";
import { _localization } from "../../../config/location";

/** Số dòng tối đa hiển thị ở bảng xem trước */
export const PREVIEW_MAX_ROW = 1000;

export type SerialImportPreview = {
  fileName: string;
  headers: string[];
  /** Tối đa PREVIEW_MAX_ROW dòng đầu của file */
  rows: string[][];
  /** Tổng số dòng dữ liệu của file (không tính dòng tiêu đề) */
  totalRow: number;
};

/** Đọc sheet đầu tiên của file excel: dòng 1 là tiêu đề, các dòng sau là dữ liệu */
export const readSerialImportPreview = (file: File) =>
  new Promise<SerialImportPreview>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Không đọc được file excel !"));
    reader.onload = (e) => {
      try {
        const workbook = xlsx.read(e.target?.result, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const matrix = worksheet
          ? xlsx.utils.sheet_to_json<any[]>(worksheet, {
              header: 1,
              defval: "",
              raw: false,
              blankrows: false,
            })
          : [];
        const [headerRow = [], ...dataRows] = matrix;
        const columnCount = Math.max(
          headerRow.length,
          ...dataRows.slice(0, PREVIEW_MAX_ROW).map((r) => r.length)
        );

        resolve({
          fileName: file.name,
          headers: Array.from(
            { length: columnCount },
            (_, i) => String(headerRow[i] ?? "").trim() || `Cột ${i + 1}`
          ),
          rows: dataRows
            .slice(0, PREVIEW_MAX_ROW)
            .map((r) => Array.from({ length: columnCount }, (_, i) => String(r[i] ?? ""))),
          totalRow: dataRows.length,
        });
      } catch (error) {
        reject(error);
      }
    };
    reader.readAsArrayBuffer(file);
  });

type ImportSerialPreviewModalProps = {
  preview: SerialImportPreview | null;
  onClose: () => void;
  onConfirm: () => void;
};

const ImportSerialPreviewModal = ({
  preview,
  onClose,
  onConfirm,
}: ImportSerialPreviewModalProps) => {
  const columns = useMemo<MRT_ColumnDef<string[]>[]>(
    () => [
      {
        id: "STT",
        header: "STT",
        size: 60,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => <>{row.index + 1}</>,
      },
      ...(preview?.headers ?? []).map<MRT_ColumnDef<string[]>>((header, index) => ({
        id: `col_${index}`,
        header,
        accessorFn: (row) => row[index],
        size: 200,
      })),
    ],
    [preview?.headers]
  );

  const table = useMantineReactTable({
    columns,
    data: preview?.rows ?? [],
    localization: _localization,
    enableTopToolbar: false,
    enableColumnResizing: true,
    enableStickyHeader: true,
    enableColumnPinning: true,
    initialState: {
      density: "xs",
      columnPinning: { left: ["STT"] },
      pagination: { pageIndex: 0, pageSize: 50 },
    },
    mantineTableContainerProps: {
      style: { height: "clamp(300px, 58vh, 620px)" },
    },
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["50", "100", "200"],
    },
    paginationDisplayMode: "pages",
    mantineTableProps: { striped: false },
  });

  const overLimit = (preview?.totalRow ?? 0) > PREVIEW_MAX_ROW;

  return (
    <Modal
      opened={!!preview}
      onClose={onClose}
      size="90%"
      title={<Text fw={700}>Xem trước dữ liệu import</Text>}
    >
      {/* Dùng padding thay margin để không bị margin-collapse với body của modal */}
      <Group gap="xs" style={{ paddingTop: 12, paddingBottom: 16 }}>
        <IconFileSpreadsheet size={18} color="#15aabf" />
        <Text size="sm" fw={500}>
          {preview?.fileName}
        </Text>
        <Badge variant="light" color="blue" size="sm">
          {preview?.totalRow.toLocaleString("vi-VN")} dòng
        </Badge>
      </Group>

      {overLimit ? (
        <Alert
          variant="light"
          color="orange"
          icon={<IconAlertTriangle size={18} />}
          mb="md"
        >
          <Text size="sm">
            File có {preview?.totalRow.toLocaleString("vi-VN")} dòng dữ liệu,
            bảng dưới chỉ hiển thị {PREVIEW_MAX_ROW.toLocaleString("vi-VN")} dòng
            đầu. Vui lòng mở file để kiểm tra lại{" "}
            {((preview?.totalRow ?? 0) - PREVIEW_MAX_ROW).toLocaleString("vi-VN")}{" "}
            dòng còn lại trước khi xác nhận import.
          </Text>
        </Alert>
      ) : null}

      <MantineReactTable table={table} />

      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onClose}>
          Hủy
        </Button>
        <Button leftSection={<IconCheck size={16} />} onClick={onConfirm}>
          Xác nhận import
        </Button>
      </Group>
    </Modal>
  );
};

export default ImportSerialPreviewModal;
